import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useReducer
} from 'react';

import {
  SubscriptionResponseV2,
  SubscriptionState
} from 'src/types/subscription';

export const SubscriptionContext = createContext<SubscriptionState[] | null>(
  null
);
export const SubscriptionDispatchContext = createContext<Dispatch<
  SubscriptionAction<SubscriptionState[]>
> | null>(null);

export const SubscriptionContextV2 = createContext<
  SubscriptionResponseV2.DurationState[] | null
>(null);
export const SubscriptionDispatchContextV2 = createContext<Dispatch<
  SubscriptionAction<SubscriptionResponseV2.DurationState[]>
> | null>(null);

interface SubscriptionAction<T> {
  type: SUBSCRIPTION_ACTIONS;
  payload: {
    id?: string;
    data?: T;
  };
}

export const SubscriptionProvider = ({
  children,
  initialState
}: {
  children: ReactNode;
  initialState: SubscriptionState[];
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <SubscriptionContext.Provider value={state}>
      <SubscriptionDispatchContext.Provider value={dispatch}>
        {children}
      </SubscriptionDispatchContext.Provider>
    </SubscriptionContext.Provider>
  );
};

export const SubscriptionProviderV2 = ({
  children,
  initialState
}: {
  children: ReactNode;
  initialState: SubscriptionResponseV2.DurationState[];
}) => {
  const [state, dispatch] = useReducer(reducerV2, initialState);
  return (
    <SubscriptionContextV2.Provider value={state}>
      <SubscriptionDispatchContextV2.Provider value={dispatch}>
        {children}
      </SubscriptionDispatchContextV2.Provider>
    </SubscriptionContextV2.Provider>
  );
};

const reducerV2 = (
  state: SubscriptionResponseV2.DurationState[],
  action: SubscriptionAction<SubscriptionResponseV2.DurationState[]>
) => {
  switch (action.type) {
    case SUBSCRIPTION_ACTIONS.UPDATE_SUBSCTIPTION: {
      return action.payload.data || state;
    }
    case SUBSCRIPTION_ACTIONS.UPDATE_DURATION: {
      return state.map((item) => ({
        ...item,
        active: item.id === action.payload.id
      }));
    }
    case SUBSCRIPTION_ACTIONS.UPDATE_SELECTED_PLAN: {
      return state.map((item) =>
        item.active
          ? {
              ...item,
              options: item.options.map((option) => ({
                ...option,
                active: option.id === action.payload.id
              }))
            }
          : item
      );
    }
    case SUBSCRIPTION_ACTIONS.UPDATE_PROMO_PLAN: {
      const activeDuration = state.find(
        (item) => !!item.options.find((plan) => plan.id === action.payload.id)
      );
      if (activeDuration?.id) {
        return state.map((item) =>
          item.id === activeDuration.id
            ? {
                ...item,
                active: true,
                options: item.options.map((option) => ({
                  ...option,
                  active: option.id === action.payload.id
                }))
              }
            : {
                ...item,
                active: false
              }
        );
      }
      return state;
    }
    default:
      return state;
  }
};

const reducer = (
  state: SubscriptionState[],
  action: SubscriptionAction<SubscriptionState[]>
) => {
  switch (action.type) {
    case SUBSCRIPTION_ACTIONS.UPDATE_DURATION: {
      return state.map((item) =>
        item.active
          ? {
              ...item,
              options: item.options.map((option) => ({
                ...option,
                active: option.id === action.payload.id
              }))
            }
          : item
      );
    }
    case SUBSCRIPTION_ACTIONS.UPDATE_CATEGORY: {
      return state.map((item) => ({
        ...item,
        active: item.id === action.payload.id
      }));
    }
    case SUBSCRIPTION_ACTIONS.UPDATE_SELECTED_PLAN: {
      return state.map((item) => {
        const isSelectedCategory = item?.options.find(
          (option) => option.id === action.payload.id
        );
        return isSelectedCategory
          ? {
              ...item,
              options: item.options.map((option) => ({
                ...option,
                active: option.id === action.payload.id
              })),
              active: true
            }
          : {
              ...item,
              active: false
            };
      });
    }
    case SUBSCRIPTION_ACTIONS.UPDATE_SUBSCTIPTION: {
      return action.payload.data || state;
    }
    default:
      return state;
  }
};

export const useSubscriptionState = () => {
  return useContext(SubscriptionContext);
};

export const useSubscriptionDispatch = () => {
  return useContext(SubscriptionDispatchContext) as Dispatch<
    SubscriptionAction<SubscriptionState[]>
  >;
};

export const useSubscriptionStateV2 = () => {
  return useContext(SubscriptionContextV2);
};

export const useSubscriptionDispatchV2 = () => {
  return useContext(SubscriptionDispatchContextV2) as Dispatch<
    SubscriptionAction<SubscriptionResponseV2.DurationState[]>
  >;
};

export enum SUBSCRIPTION_ACTIONS {
  UPDATE_DURATION,
  UPDATE_CATEGORY,
  UPDATE_SUBSCTIPTION,
  UPDATE_SELECTED_PLAN,
  UPDATE_PROMO_PLAN
}
